import type { CSSProperties } from 'react';

import { isSameDay } from 'date-fns';

import type { CalendarEvent, EventColor } from '@/components/event-calendar';

/**
 * Get CSS classes for event colors
 */
export function getEventColorClasses(color?: EventColor | string): string {
    const eventColor = color || 'sky';

    if (eventColor.startsWith('#')) {
        return '';
    }

    switch (eventColor) {
        case 'sky':
            return 'bg-sky-500/80 hover:bg-sky-500/70 text-white dark:bg-sky-400/70 dark:text-white shadow-sky-700/25';
        case 'amber':
            return 'bg-amber-500/80 hover:bg-amber-500/70 text-white dark:bg-amber-400/70 dark:text-white shadow-amber-700/25';
        case 'violet':
            return 'bg-violet-500/80 hover:bg-violet-500/70 text-white dark:bg-violet-400/70 dark:text-white shadow-violet-700/25';
        case 'rose':
            return 'bg-rose-500/80 hover:bg-rose-500/70 text-white dark:bg-rose-400/70 dark:text-white shadow-rose-700/25';
        case 'emerald':
            return 'bg-emerald-500/80 hover:bg-emerald-500/70 text-white dark:bg-emerald-400/70 dark:text-white shadow-emerald-700/25';
        case 'orange':
            return 'bg-orange-500/80 hover:bg-orange-500/70 text-white dark:bg-orange-400/70 dark:text-white shadow-orange-700/25';
        case 'utility-gray':
            return 'bg-gray-300/80 hover:bg-gray-300/70 text-gray-900 dark:bg-gray-400/80 dark:text-gray-950 shadow-gray-700/25';
        default:
            return 'bg-sky-500/80 hover:bg-sky-500/70 text-white dark:bg-sky-400/70 dark:text-white shadow-sky-700/25';
    }
}

export function getEventColorInlineStyle(
    color?: EventColor | string,
    options?: { forceDarkText?: boolean },
): CSSProperties | undefined {
    if (!color || !color.startsWith('#'))
        return undefined;

    const normalized = color.toLowerCase();
    const textColor = options?.forceDarkText ? '#0f172a' : '#ffffff';
    const textShadow = textColor === '#ffffff'
        ? '0 1px 2px rgba(15, 23, 42, 0.45)'
        : undefined;

    return {
        backgroundColor: normalized,
        color: textColor,
        textShadow,
    } satisfies CSSProperties;
}

/**
 * Get CSS classes for border radius based on event position in multi-day events
 */
export function getBorderRadiusClasses(
    isFirstDay: boolean,
    isLastDay: boolean,
): string {
    if (isFirstDay && isLastDay) {
        return 'rounded'; // Both ends rounded
    }
    else if (isFirstDay) {
        return 'rounded-l rounded-r-none'; // Only left end rounded
    }
    else if (isLastDay) {
        return 'rounded-r rounded-l-none'; // Only right end rounded
    }
    else {
        return 'rounded-none'; // No rounded corners
    }
}

/**
 * Check if an event is a multi-day event
 */
export function isMultiDayEvent(event: CalendarEvent): boolean {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end ?? event.start);
    return event.allDay || eventStart.getDate() !== eventEnd.getDate();
}

/**
 * Filter events for a specific day
 */
export function getEventsForDay(
    events: CalendarEvent[],
    day: Date,
): CalendarEvent[] {
    return events
        .filter((event) => {
            const eventStart = new Date(event.start);
            return isSameDay(day, eventStart);
        })
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
}

/**
 * Sort events with multi-day events first, then by start time
 */
export function sortEvents(events: CalendarEvent[]): CalendarEvent[] {
    return [...events].sort((a, b) => {
        const aIsMultiDay = isMultiDayEvent(a);
        const bIsMultiDay = isMultiDayEvent(b);

        if (aIsMultiDay && !bIsMultiDay)
            return -1;
        if (!aIsMultiDay && bIsMultiDay)
            return 1;

        return new Date(a.start).getTime() - new Date(b.start).getTime();
    });
}

/**
 * Get multi-day events that span across a specific day (but don't start on that day)
 */
export function getSpanningEventsForDay(
    events: CalendarEvent[],
    day: Date,
): CalendarEvent[] {
    return events.filter((event) => {
        if (!isMultiDayEvent(event))
            return false;

        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end ?? event.start);

        // Only include if it's not the start day but is either the end day or a middle day
        return (
            !isSameDay(day, eventStart)
            && (isSameDay(day, eventEnd) || (day > eventStart && day < eventEnd))
        );
    });
}

/**
 * Get all events visible on a specific day (starting, ending, or spanning)
 */
export function getAllEventsForDay(
    events: CalendarEvent[],
    day: Date,
): CalendarEvent[] {
    return events.filter((event) => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end ?? event.start);
        return (
            isSameDay(day, eventStart)
            || isSameDay(day, eventEnd)
            || (day > eventStart && day < eventEnd)
        );
    });
}

/**
 * Get all events for a day (for agenda view)
 */
export function getAgendaEventsForDay(
    events: CalendarEvent[],
    day: Date,
): CalendarEvent[] {
    return events
        .filter((event) => {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end ?? event.start);
            return (
                isSameDay(day, eventStart)
                || isSameDay(day, eventEnd)
                || (day > eventStart && day < eventEnd)
            );
        })
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
}
